import invariant from 'tiny-invariant';

const formatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  // second: 'numeric',
  hour12: true,
  timeZoneName: 'short',
};
const dateFormatter = new Intl.DateTimeFormat('en-gb', formatOptions);

export function formatBool(boolVal) {
  return (boolVal == true) ? 'true' : ((boolVal == false) ? 'false' : '');
}

export function formatDate(dateString) {
  return dateFormatter.format(new Date(dateString));
  // Alternative date format -- ISO standard but in local time:
  // return new Date(dateString).toLocaleString( 'sv', { timeZoneName: 'short' } );
}

export function formatDuration(duration) {
  // Format a duration in seconds as a colon-separated string H:M:S
  return new Date(1000*duration).toISOString().substr(11, 8);
}

export function formatCost(cost) {
  return cost > 0.1 ? `${cost.toFixed(2)}` : `${cost.toFixed(4)}`;
}

export function formatPlural(count, singular, plural, opts) {
  const defaultOpts = { head: '', tail: '', blankIfZero: false };
  const { head, tail, blankIfZero } = { ...defaultOpts, ...opts };
  if (blankIfZero && count === 0) {
    return '';
  } else {
    return `${head}${count} ${count === 1 ? singular : plural}${tail}`;
  }
}

export function shortUrl(url, length=50) {
  if (url.length <= length) {
    return url;
  }
  const startLength = Math.floor((length - 3) / 2);
  const endLength = length - 3 - startLength;
  return url.slice(0, startLength) + '...' + url.slice(-endLength);
}

export function round(value, precision) {
  var factor = Math.pow(10, precision ?? 0);
  return Math.round(value * factor) / factor;
}

const dirtyFilenameRE  = /[^A-Za-z0-9_\-\.]/;
const dirtyFilenameREG = /[^A-Za-z0-9_\-\.]+/g;

export function checkCleanFilename(filename) {
  return (
    (typeof filename === 'string' || filename instanceof String) &&
    (filename.length > 0) &&
    (!(dirtyFilenameRE).test(filename)) &&
    (!filename.startsWith('-'))
  );
}

export function makeCleanFilename(filename) {
  const withoutLeadingDash = filename.replace(/^-/, '_');
  const cleanFilename = withoutLeadingDash.replaceAll(dirtyFilenameREG, '_');
  invariant(checkCleanFilename(cleanFilename), `Filename not clean: ${filename}`);
  return cleanFilename;
}

export function splitExt(filename) {
  invariant(filename.length > 0, `Filename cannot be empty: ${filename}`);
  let splitName = filename.split('.');
  let root, ext;
  if (splitName.length === 1) {
    root = filename;
    ext = '';
  } else {
    invariant(splitName.length > 1, `Filename must have an extension: ${filename}`);
    ext = '.' + splitName.pop();
    root = splitName.join('.');
  }
  return [ root, ext ];
}

export function folderDisplayName(folder) {
  // Get the display name of a folder
  const rootFolder = 'Materials';
  return folder ? (rootFolder + '/' + folder) : rootFolder;
}

export function folderMatches(folder, folder2) {
  // Return true if the folder names match, allowing for undefined or empty folder names
  // referring to the root folder
  return ((folder === folder2) || (!folder && !folder2));
}

export function folderIsChild(folderA, folderB) {
  // Return true if folderB is an immediate child of folderA
  if (folderMatches(folderA, folderB)) {
    return false;
  }
  var parts = folderA.split('/');
  var parts2 = folderB.split('/');
  if (parts.length === 1 && parts[0] === '') {
    parts = [];
  }
  if (parts2.length !== parts.length + 1) {
    return false;
  }
  parts.forEach((dir, i) => {
    if (parts2[i] !== dir) {
      return false;
    }
  });
  return true;
}

// Keep synchronised with TextureType values and textype_get_default_constants() in Bach:scripts/textype.py
export const TextureTypes = new Map(Object.entries({
  'Alpha': [1.0],
  'Ambient occlusion': [1.0],
  'Basecolor': [0.5, 0.5, 0.5],
  'Displacement': [0.5],
  'Metallic': [0.0],
  'Normals': [0.5, 0.5, 1.0],
  'Roughness': [1.0],
}));

export const PseudoTextureTypes = new Map(Object.entries({
  'Glossiness': 'Roughness',
}));

export const AllTextureTypes = [...TextureTypes.keys(), ...PseudoTextureTypes.keys()].sort();

// Texture types accepted by the tiling CLI for control point generation/refinement. Keep synchronised with
// textype.py (TextureType enum) and texport.py (control_point_tex_types). TODO: once pbr-extended merges
// (TextureTypes keys become lowercase), replace with [...TextureTypes.keys()] filtered as needed (e.g. we
// don't show the points texture type to users for control point generation).
export const ControlPointTextureTypes = [
  'basecolor', 'normals', 'specular', 'displacement', 'roughness', 'metallic', 'aomap', 'alpha',
];

export function textureTypeFromFilename(filename) {
  // Returns an Array of:
  // - textureType (string enum)
  // - filename with the texture type suffix and extension removed

  // Keep synchronised with SUFFIXES_SYNC in Bach:scripts/texin.py
  const SUFFIXES = {
    'ALPHA': 'Alpha',
    'OPACITY': 'Alpha',
    'MASK': 'Alpha',

    'AMBIENTOCCLUSION': 'Ambient occlusion',
    'AMBIENT_OCCLUSION': 'Ambient occlusion',
    'AO': 'Ambient occlusion',
    'OCCLUSION': 'Ambient occlusion',

    'ALBEDO': 'Basecolor',
    'BASE': 'Basecolor',
    'BASECOLOR': 'Basecolor',
    'BASECOLOUR': 'Basecolor',
    'BASE_COLOR': 'Basecolor',
    'BASE_COLOUR': 'Basecolor',
    'COL': 'Basecolor',
    'COLOR': 'Basecolor',
    'COLOUR': 'Basecolor',
    'DIFF': 'Basecolor',
    'DIFFUSE': 'Basecolor',

    'DISP': 'Displacement',
    'DISPLACEMENT': 'Displacement',
    'HEIGHT': 'Displacement',
    'HEIGHTMAP': 'Displacement',

    'GLOSS': 'Glossiness',
    'GLOSSINESS': 'Glossiness',

    'MTL': 'Metallic',
    'METAL': 'Metallic',
    'METALLIC': 'Metallic',
    'METALNESS': 'Metallic',

    'NRM': 'Normals',
    'NORM': 'Normals',
    'NORMAL': 'Normals',

    'ROUGH': 'Roughness',
    'ROUGHNESS': 'Roughness',
  }
  if (!filename) {
    return ['', ''];
  }
  const [base, ext] = splitExt(filename);
  if (!base) {
    return ['', ''];
  }

  // Find all suffix matches, and select the match that appears last in the filename
  let match = '';
  let matchPosition = -1;
  let textureType = '';
  for (const [suffix, type] of Object.entries(SUFFIXES)) {
    const position = base.toUpperCase().lastIndexOf(suffix);
    if (position > matchPosition) {
      match = suffix;
      matchPosition = position;
      textureType = type;
    }
  }

  if (match !== '') {
    const basePrefix = base.slice(0, matchPosition);
    const basePrefixClean = basePrefix.replace(/[ _\.\-]+$/, '');
    return [textureType, basePrefixClean];
  } else {
    // We return '' for unknown types, which is what Material UI Select wants for the unselected state.
    return ['', base.replace(/[ _\.\-]+$/, '')];
  }
}
